import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { useSubscriptions, useCreatePayment, usePaymentHistory } from '../hooks/useApi'
import { useAuth } from '../hooks/authContext'
import { toast } from 'sonner'
import { Database, Check, Crown } from 'lucide-react'
import { PLANS, getPlanDisplayName, normalizePlanId, PLAN_FEATURES } from '../lib/plans'

const API_URL = import.meta.env.VITE_API_URL || '/api'
const INPUT_DATA_FEE = 35000

export default function Subscription() {
  const { user } = useAuth()
  const { data: subscriptions } = useSubscriptions()
  const { data: paymentHistory = [] } = usePaymentHistory()
  const createPayment = useCreatePayment()

  const currentSub = subscriptions?.[0]
  const currentPlanId = normalizePlanId(currentSub?.plan)
  const currentPlanName = getPlanDisplayName(currentSub?.plan)

  const handleSubscribe = async (plan: typeof PLANS[0]) => {
    if (plan.price === 0) {
      const response = await fetch(`${API_URL}/subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ plan: plan.id }),
      })
      if (response.ok) {
        toast.success('Paket Gratis aktif! Selamat menggunakan platform.')
        window.location.reload()
      } else {
        const error = await response.json()
        toast.error(error.error || 'Gagal mengaktifkan paket')
      }
      return
    }

    await createPayment.mutateAsync({
      amount: plan.price,
      paymentType: 'subscription',
      plan: plan.id,
    })
  }

  const handleInputDataPayment = async () => {
    await createPayment.mutateAsync({
      amount: INPUT_DATA_FEE,
      paymentType: 'data_input',
    })
  }

  const isPaying = createPayment.isPending

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Pilih Paket Berlangganan</h1>
      <p className="text-gray-600 mb-8">
        Bangun website katalog usaha, tampil profesional, dan mudah dibagikan ke pelanggan.
      </p>

      {currentSub && (
        <Card className="mb-8 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Paket Aktif: <span className="text-indigo-700">{currentPlanName}</span>
            </h2>
            <p className="text-gray-600 mt-1">Status: {currentSub.isActive !== false ? 'Aktif' : 'Tidak aktif'}</p>
            {currentSub.endDate && (
              <p className="text-gray-600">
                Berlaku hingga: {new Date(currentSub.endDate).toLocaleDateString('id-ID')}
              </p>
            )}
            <ul className="mt-4 grid sm:grid-cols-2 gap-2">
              {(currentSub.features || PLAN_FEATURES[currentPlanId]).map((f: string, i: number) => (
                <li key={i} className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 mr-2 text-green-500 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-8 mb-10">
        {PLANS.map((plan) => (
          <Card
            key={plan.id}
            className={`relative ${plan.popular ? 'border-2 border-indigo-500 shadow-xl' : ''} ${currentPlanId === plan.id ? 'ring-2 ring-indigo-300' : ''}`}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                <Crown className="w-3 h-3" /> Rekomendasi
              </div>
            )}
            <CardHeader>
              <CardTitle>{plan.name}</CardTitle>
              <p className="text-sm text-gray-500">{plan.description}</p>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900 mb-4">
                {plan.price === 0 ? (
                  <span className="text-green-600">Rp0</span>
                ) : (
                  <>
                    Rp {plan.price.toLocaleString('id-ID')}
                    <span className="text-sm font-normal text-gray-500">/tahun</span>
                  </>
                )}
              </p>
              <ul className="space-y-2 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start text-gray-600 text-sm">
                    <Check className="w-4 h-4 mr-2 text-green-500 shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => handleSubscribe(plan)}
                disabled={(isPaying && plan.price > 0) || currentPlanId === plan.id}
                className={`w-full ${plan.popular ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                variant={plan.price === 0 ? 'outline' : 'default'}
              >
                {currentPlanId === plan.id
                  ? 'Paket Aktif'
                  : plan.price === 0
                    ? 'Paket Gratis'
                    : isPaying
                      ? 'Memproses...'
                      : `Berlangganan ${plan.name}`}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {paymentHistory.length > 0 && (
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Riwayat Pembayaran</h2>
          <Card>
            <CardContent className="pt-6">
              <ul className="divide-y">
                {paymentHistory.map((p: any) => (
                  <li key={p.id} className="py-3 flex justify-between text-sm">
                    <span>
                      {p.paymentType === 'subscription' ? 'Langganan' : 'Input Data'} — {p.orderId}
                    </span>
                    <span className="font-medium">
                      Rp {parseInt(p.amount).toLocaleString('id-ID')} — {p.status}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      )}

      <h2 className="text-2xl font-bold text-gray-900 mb-4">Layanan Tambahan</h2>
      <Card className="max-w-xl border-amber-200 bg-amber-50/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-amber-700" />
            Pembayaran Input Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-3xl font-bold text-gray-900 mb-2">
            Rp {INPUT_DATA_FEE.toLocaleString('id-ID')}
            <span className="text-sm font-normal text-gray-500 ml-2">sekali bayar</span>
          </p>
          <p className="text-gray-600 mb-4">
            Layanan bantuan input data produk ke sistem oleh tim kami. Pembayaran satu kali,
            diproses aman melalui Midtrans.
          </p>
          <Button onClick={handleInputDataPayment} disabled={isPaying} className="w-full sm:w-auto">
            {isPaying ? 'Memproses...' : 'Bayar Input Data'}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
